/* eslint-disable max-len */
import { DateTime } from 'luxon'
import { PROVINCES, PROVINCES_BY_COUNTRY } from './data/places'

/**
 * DataFaker is a service used to generate large amounts of pseudorandom test data
 * useful in building unit tests and mocking elements. It all operations use a seed
 * int, and the same operation with the same seed is deterministic.
 */
export class DataFaker {
  static ADJECTIVES = ['handcrafted', 'unbranded','fantastic','gorgeous','ergonomic','tasty','licensed','ergonomic','small','incredible','handmade','tasty','practical','generic','practical','incredible','handmade','practical','handmade','tasty','practical','incredible','unbranded','awesome','refined','sleek','rustic','ergonomic','handcrafted','incredible','ergonomic','refined','awesome','ergonomic','fantastic','gorgeous','ergonomic','awesome','handcrafted','generic','fantastic','handmade','awesome','ergonomic','generic','ergonomic','tasty','licensed','ergonomic','handcrafted','ergonomic','fantastic','handcrafted','sleek','refined','sleek','refined','intelligent','generic','handcrafted','fantastic','small','incredible','small','unbranded']
  static BOOLEANS = [true, false, false, true, true, true, false, true, false, false, true, false, false, false, true]
  static DATE_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  static DATE_DAYS = [1,2,6,7,8,3,11,31,23,26,12,14,19,22]
  static DATE_HOURS = [0,5,6,7,4,3,22,23,22,18,15,16,12,1,2,8,9,10,11,2,6]
  static DATE_MINUTES = [0,15,20,25,20,15,45,30,45,15,0,0,15,0,0,0,0,15,30]
  static DATE_MONTHS = [1,2,3,6,7,8,9,5,4,5,6,7,8,8,9,12,11,10,1,2,3,4,5,9,10,3]
  static DATE_YEARS = [2020, 2019, 2020, 2019, 2018, 2019, 2020, 2019, 2018, 2016]
  static DRINKS = ['Whiskey', 'Beer', 'Wine', 'Gin', 'Vodka', 'Brandy', 'Rum', 'Sake', 'Irish Whiskey', 'Soju', 'Ale', 'Lager', 'Plum Wine', 'Palinka', 'Vodka', 'Stout', 'Cider', 'Sake', 'Tonto', 'Port', 'Sherry', 'Vermouth', 'Sangria', 'Mead', 'Madeira', 'Rum', 'Whisky', 'Mirin', 'Absinthe', 'Ouzo', 'Raki', 'Sambuca', 'Arrack', 'Mezcal', 'Tequila', 'Schnapps', 'Maotai']
  static EMAILS = ['Mckenzie_Murphy60@gmail.com', 'Jaylin.Armstrong@gmail.com', 'Hubert66@gmail.com', 'Mylene.Jacobson@gmail.com', 'Esteban_Langworth@yahoo.com', 'Kaia_Bode54@gmail.com', 'Mollie.Tremblay@yahoo.com', 'Queenie_Kessler80@gmail.com', 'Jairo_Price29@yahoo.com', 'William44@hotmail.com', 'Laurianne84@gmail.com', 'Kristina.Schamberger@gmail.com', 'Tatyana_Heathcote60@gmail.com', 'Willis.Gutmann38@yahoo.com', 'Rosalinda.Russel@hotmail.com', 'Nash67@yahoo.com', 'Clement53@hotmail.com', 'Shayna46@gmail.com', 'Kenton66@gmail.com', 'Adam.Hessel63@hotmail.com', 'Estell.Schuppe30@gmail.com', 'Bradly.McClure@hotmail.com', 'Mayra_Wyman25@gmail.com', 'Amir_White13@gmail.com', 'Orland10@yahoo.com', 'Nash.Bailey@yahoo.com', 'Jessy_Breitenberg7@hotmail.com', 'Clark20@yahoo.com', 'Orpha12@yahoo.com', 'Nicklaus_Erdman@hotmail.com', 'Celestino27@hotmail.com', 'Baron_Kozey45@gmail.com', 'Emerson.Ferry@hotmail.com', 'Hillary15@yahoo.com', 'Macie_Schowalter@gmail.com', 'Jay.Feest@gmail.com']
  static HEX_CHARACTERS = '0123456789abcdef'.split('')
  static HEXES = ['#f76061', '#386d0e', '#d501af', '#2e90a4']
  static NAMES = ['Alice', 'Bob', 'Mitchell', 'Cameron', 'Aiden', 'Brent', 'Bryan', 'Abdul', 'Mildred', 'Eva', 'Martin']
  static NOUNS = ['Fish', 'Towels', 'Gloves', 'Table', 'Chips', 'Tuna', 'Salad', 'Mouse', 'Table', 'Bacon', 'Chips', 'Fish', 'Sausages', 'Bacon', 'Gloves', 'Towels', 'Sausages', 'Chicken', 'Sausages', 'Bacon']
  static PROVINCES = PROVINCES
  static PROVINCES_BY_COUNTRY = PROVINCES_BY_COUNTRY
  static VERBS = ['Get', 'Interpret', 'Mean', 'Flood', 'Breathe', 'Murmur', 'Argue', 'Cry', 'Specify', 'Conform', 'Encourage', 'Withdraw', 'Retain', 'Dominate', 'Arrange', 'Tie', 'Resign', 'Allocate', 'Entitle', 'Travel', 'Shout', 'Set', 'Exclude', 'Confer', 'Shiver', 'Eat']
  static WEB_DOMAIN_SUFFIXES = ['.com', '.co.uk', '.net', '.org', '.com', '.com.ar', '.eu', '.com', '.com']
  static WEB_PROTOCOLS = ['http://', 'https://']

  /**
   * Returns a deterministic random value (same seed will always return the same array choice)
   * Useful for generating test data
   * @param values an array of anything
   * @param seed a seed to pluck a pseudorandom value out
   */
  static gen(values: any[], seed: number) {
    return values[seed % values.length]
  }

  /**
   * Generate an array of values providing a factory function that accepts the seed number
   * @param factory a function that returns an item type T
   * @param count the number to gen
   */
  static genFactory<T>(factory: (seed: number) => T, count: number) {
    const state: T[] = []
    for (let i = 0; i < count; i++) {
      const val = factory(i)
      state.push(val)
    }
    return state
  }

  /**
   * Pass a schema that contains key: value where the value is an array of possible values.
   * It will return count pseudorandomly generated objects matching the schema
   * @param schema A key value of arrays to provide possible options
   * @param count The number of rows to generate
   */
  static genN(schema: { [key: string]: any[] }, count: number): any[] {
    const state = []
    const keys = Object.keys(schema)
    for (let i = 0; i < count; i++) {
      const entity: any = {}
      for (let j = 0; j < keys.length; j++) {
        const key = keys[j]
        entity[key] = DataFaker.gen(schema[key], i + j)
      }
      state.push(entity)
    }
    return state
  }

  /**
   * Generates a deterministic uuid from a seed and a key
   * key ensures that you don't get the same uuid for a different type 
   * (i.e. genUuid(1, 'page') !== genUuid(1, 'user'))
   * @param seed a number to seed
   * @param key a string to modify the seed by
   */
  static genUuid(seed: number, key: string): string {
    const base = 'e9f9a95f-16e2-45b6-a06f-d385f91a969f'
    let generated = ''

    for (let i = 0; i < base.length; i++) {
      const char = base[i]
      const keySeed = key.codePointAt(i + seed % key.length) || i
      // console.log({ keyCharacters, seed, key, generated, i, keySeed, char })

      if (char === '-') {
        generated += '-'
      } else {
        generated += DataFaker.gen(DataFaker.HEX_CHARACTERS, seed + i + keySeed)
      }
    }
    return generated
  }

  /**
   * Turns an input string into a seed number based on the sum of all its characters
   * @param input any string
   */
  static genSeed(input: string) {
    return input.split('').reduce((seed, char, i) => seed + input.charCodeAt(i), 0)
  }

  DATES = DataFaker.genFactory((seed: number) => DateTime.fromObject({
    year: DataFaker.gen(DataFaker.DATE_YEARS, seed),
    month: DataFaker.gen(DataFaker.DATE_MONTHS, seed),
    day: DataFaker.gen(DataFaker.DATE_DAYS, seed),
    hour: DataFaker.gen(DataFaker.DATE_HOURS, seed),
    minute: DataFaker.gen(DataFaker.DATE_MINUTES, seed),
  }).toISO(), 50)

  URLS = DataFaker.genFactory((seed: number) => [
    DataFaker.gen(DataFaker.WEB_PROTOCOLS, seed),
    DataFaker.gen(DataFaker.ADJECTIVES, seed),
    DataFaker.gen(DataFaker.WEB_DOMAIN_SUFFIXES, seed),
  ].join(''), 150)
}
